(() => {
  if (window.__sizeGuideZoomViewerInitialized) return;
  window.__sizeGuideZoomViewerInitialized = true;

  const IMAGE_SELECTOR = '.product-popup-modal[open] .product-popup-modal__content-info img';
  const MAX_SCALE = 4;
  const DOUBLE_TAP_MS = 280;
  const HINT_HIDE_DELAY_MS = 3200;
  const TAP_MOVE_THRESHOLD = 10;
  const TAP_SCROLL_THRESHOLD = 10;
  const TAP_MAX_DURATION_MS = 300;
  const TAP_VALID_WINDOW_MS = 700;

  let viewer;
  let stage;
  let zoomImage;
  let closeButton;
  let backdrop;
  let controlsElement;
  let hintElement;
  let hintTimerId;

  const state = {
    isOpen: false,
    scale: 1,
    x: 0,
    y: 0,
    baseWidth: 1,
    baseHeight: 1,
    stageWidth: 1,
    stageHeight: 1,
    isMousePanning: false,
    isTouchPanning: false,
    isTouchPinching: false,
    panStartPointerX: 0,
    panStartPointerY: 0,
    panStartX: 0,
    panStartY: 0,
    pinchStartDistance: 0,
    pinchStartScale: 1,
    pinchStartX: 0,
    pinchStartY: 0,
    pinchCenterX: 0,
    pinchCenterY: 0,
    lastTouchEndAt: 0,
    docTouchStartX: 0,
    docTouchStartY: 0,
    docTouchStartAt: 0,
    docTouchEndAt: 0,
    docTouchStartScrollY: 0,
    docTouchMoved: false,
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const touchDistance = (firstTouch, secondTouch) => {
    const dx = secondTouch.clientX - firstTouch.clientX;
    const dy = secondTouch.clientY - firstTouch.clientY;
    return Math.hypot(dx, dy);
  };

  const touchMidpoint = (firstTouch, secondTouch) => {
    return {
      clientX: (firstTouch.clientX + secondTouch.clientX) / 2,
      clientY: (firstTouch.clientY + secondTouch.clientY) / 2,
    };
  };

  const buildViewer = () => {
    if (viewer) return;

    viewer = document.createElement('div');
    viewer.className = 'size-guide-zoom-viewer';
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute('aria-label', 'Visor de guia de tallas');

    viewer.innerHTML = `
      <div class="size-guide-zoom-viewer__backdrop" data-size-guide-zoom-close></div>
      <div class="size-guide-zoom-viewer__shell">
        <div class="size-guide-zoom-viewer__stage" data-size-guide-zoom-stage>
          <img alt="" draggable="false" data-size-guide-zoom-image>
          <div class="size-guide-zoom-viewer__controls" data-size-guide-zoom-controls>
            <button type="button" class="size-guide-zoom-viewer__btn size-guide-zoom-viewer__btn--close" data-size-guide-zoom-close-btn>Ocultar</button>
          </div>
          <p class="size-guide-zoom-viewer__hint is-hidden" data-size-guide-zoom-hint>
            Pellizca para zoom, arrastra para mover, doble tap para acercar.
          </p>
        </div>
      </div>
    `;

    document.body.appendChild(viewer);

    stage = viewer.querySelector('[data-size-guide-zoom-stage]');
    controlsElement = viewer.querySelector('[data-size-guide-zoom-controls]');
    zoomImage = viewer.querySelector('[data-size-guide-zoom-image]');
    closeButton = viewer.querySelector('[data-size-guide-zoom-close-btn]');
    backdrop = viewer.querySelector('.size-guide-zoom-viewer__backdrop');
    hintElement = viewer.querySelector('[data-size-guide-zoom-hint]');

    closeButton.addEventListener('click', closeViewer);
    backdrop.addEventListener('click', closeViewer);

    document.addEventListener('keydown', (event) => {
      if (!state.isOpen) return;
      if (event.key === 'Escape') closeViewer();
    });

    stage.addEventListener('wheel', handleWheel, { passive: false });
    stage.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    stage.addEventListener('touchstart', handleTouchStart, { passive: false });
    stage.addEventListener('touchmove', handleTouchMove, { passive: false });
    stage.addEventListener('touchend', handleTouchEnd, { passive: false });
    stage.addEventListener('touchcancel', handleTouchCancel, { passive: false });
    stage.addEventListener('click', handleStageClick);
  };

  const clearHintTimer = () => {
    if (!hintTimerId) return;
    window.clearTimeout(hintTimerId);
    hintTimerId = undefined;
  };

  const hideHint = () => {
    if (!hintElement) return;
    hintElement.classList.add('is-hidden');
    clearHintTimer();
  };

  const showHintTemporarily = () => {
    if (!hintElement) return;
    clearHintTimer();
    hintElement.classList.remove('is-hidden');
    hintTimerId = window.setTimeout(() => {
      hintElement.classList.add('is-hidden');
      hintTimerId = undefined;
    }, HINT_HIDE_DELAY_MS);
  };

  const updateStageMetrics = () => {
    state.stageWidth = stage.clientWidth || 1;
    state.stageHeight = stage.clientHeight || 1;
  };

  const fitImageToStage = () => {
    updateStageMetrics();

    const naturalWidth = zoomImage.naturalWidth || state.stageWidth;
    const naturalHeight = zoomImage.naturalHeight || state.stageHeight;
    const fitScale = Math.min(state.stageWidth / naturalWidth, state.stageHeight / naturalHeight);

    state.baseWidth = Math.max(1, naturalWidth * fitScale);
    state.baseHeight = Math.max(1, naturalHeight * fitScale);

    zoomImage.style.width = `${state.baseWidth}px`;
    zoomImage.style.height = `${state.baseHeight}px`;
  };

  const updateOverlayAnchors = () => {
    if (!stage) return;

    const scaledWidth = state.baseWidth * state.scale;
    const scaledHeight = state.baseHeight * state.scale;
    const imageTop = state.stageHeight / 2 + state.y - scaledHeight / 2;
    const imageRight = state.stageWidth / 2 + state.x + scaledWidth / 2;
    const imageCenterX = state.stageWidth / 2 + state.x;

    const topAnchor = Math.max(28, imageTop - 8);
    const rightAnchor = Math.min(state.stageWidth - 6, Math.max(12, imageRight + 18));
    const centerAnchor = Math.min(state.stageWidth - 10, Math.max(10, imageCenterX));

    if (controlsElement) {
      controlsElement.style.left = `${rightAnchor}px`;
      controlsElement.style.top = `${topAnchor}px`;
    }

    if (hintElement) {
      hintElement.style.left = `${centerAnchor}px`;
      hintElement.style.top = `${Math.max(24, topAnchor - 46)}px`;
    }
  };

  const constrainPan = () => {
    const scaledWidth = state.baseWidth * state.scale;
    const scaledHeight = state.baseHeight * state.scale;

    const maxX = Math.max(0, (scaledWidth - state.stageWidth) / 2);
    const maxY = Math.max(0, (scaledHeight - state.stageHeight) / 2);

    state.x = clamp(state.x, -maxX, maxX);
    state.y = clamp(state.y, -maxY, maxY);
  };

  const isPointInsideImage = (clientX, clientY) => {
    if (!stage) return false;
    const stageRect = stage.getBoundingClientRect();
    const localX = clientX - stageRect.left;
    const localY = clientY - stageRect.top;

    const renderedWidth = state.baseWidth * state.scale;
    const renderedHeight = state.baseHeight * state.scale;
    const left = state.stageWidth / 2 + state.x - renderedWidth / 2;
    const right = left + renderedWidth;
    const top = state.stageHeight / 2 + state.y - renderedHeight / 2;
    const bottom = top + renderedHeight;

    return localX >= left && localX <= right && localY >= top && localY <= bottom;
  };

  const renderTransform = () => {
    constrainPan();
    zoomImage.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`;
    updateOverlayAnchors();
  };

  const zoomToPoint = (targetScale, clientX, clientY) => {
    hideHint();
    const nextScale = clamp(targetScale, 1, MAX_SCALE);
    if (Math.abs(nextScale - state.scale) < 0.0001) return;

    const stageRect = stage.getBoundingClientRect();
    const offsetX = clientX - stageRect.left - state.stageWidth / 2;
    const offsetY = clientY - stageRect.top - state.stageHeight / 2;
    const ratio = nextScale / state.scale;

    state.x = (state.x - offsetX) * ratio + offsetX;
    state.y = (state.y - offsetY) * ratio + offsetY;
    state.scale = nextScale;

    renderTransform();
  };

  const resetTransform = () => {
    state.scale = 1;
    state.x = 0;
    state.y = 0;
    renderTransform();
  };

  const openViewer = (sourceImage) => {
    buildViewer();

    const source = sourceImage.currentSrc || sourceImage.src;
    if (!source) return;

    zoomImage.src = source;
    zoomImage.alt = sourceImage.alt || 'Guia de tallas';

    viewer.setAttribute('open', '');
    document.body.classList.add('size-guide-zoom-open');
    state.isOpen = true;
    showHintTemporarily();

    const onImageLoad = () => {
      fitImageToStage();
      resetTransform();
      zoomImage.removeEventListener('load', onImageLoad);
    };

    if (zoomImage.complete) {
      fitImageToStage();
      resetTransform();
    } else {
      zoomImage.addEventListener('load', onImageLoad);
    }
  };

  function closeViewer() {
    if (!viewer || !state.isOpen) return;
    state.isOpen = false;
    viewer.removeAttribute('open');
    document.body.classList.remove('size-guide-zoom-open');
    state.isMousePanning = false;
    state.isTouchPanning = false;
    state.isTouchPinching = false;
    hideHint();
  }

  const handleWheel = (event) => {
    if (!state.isOpen) return;
    hideHint();
    event.preventDefault();
    const direction = event.deltaY < 0 ? 1 : -1;
    const factor = direction > 0 ? 1.18 : 1 / 1.18;
    zoomToPoint(state.scale * factor, event.clientX, event.clientY);
  };

  const handleMouseDown = (event) => {
    if (!state.isOpen || state.scale <= 1) return;
    if (event.target.closest('[data-size-guide-zoom-controls]')) return;
    hideHint();
    event.preventDefault();
    state.isMousePanning = true;
    state.panStartPointerX = event.clientX;
    state.panStartPointerY = event.clientY;
    state.panStartX = state.x;
    state.panStartY = state.y;
  };

  const handleMouseMove = (event) => {
    if (!state.isMousePanning || !state.isOpen) return;
    state.x = state.panStartX + (event.clientX - state.panStartPointerX);
    state.y = state.panStartY + (event.clientY - state.panStartPointerY);
    renderTransform();
  };

  const handleMouseUp = () => {
    state.isMousePanning = false;
  };

  const handleTouchStart = (event) => {
    if (!state.isOpen) return;
    if (event.target.closest('[data-size-guide-zoom-controls]')) return;
    hideHint();

    if (event.touches.length === 2) {
      event.preventDefault();
      const firstTouch = event.touches[0];
      const secondTouch = event.touches[1];
      const midpoint = touchMidpoint(firstTouch, secondTouch);

      state.isTouchPinching = true;
      state.isTouchPanning = false;
      state.pinchStartDistance = touchDistance(firstTouch, secondTouch);
      state.pinchStartScale = state.scale;
      state.pinchStartX = state.x;
      state.pinchStartY = state.y;
      state.pinchCenterX = midpoint.clientX;
      state.pinchCenterY = midpoint.clientY;
      return;
    }

    if (event.touches.length === 1 && state.scale > 1) {
      event.preventDefault();
      state.isTouchPanning = true;
      state.isTouchPinching = false;
      state.panStartPointerX = event.touches[0].clientX;
      state.panStartPointerY = event.touches[0].clientY;
      state.panStartX = state.x;
      state.panStartY = state.y;
    }
  };

  const handleTouchMove = (event) => {
    if (!state.isOpen) return;
    hideHint();

    if (state.isTouchPinching && event.touches.length === 2) {
      event.preventDefault();
      const firstTouch = event.touches[0];
      const secondTouch = event.touches[1];
      const currentDistance = touchDistance(firstTouch, secondTouch);
      const scaleRatio = currentDistance / (state.pinchStartDistance || currentDistance);
      const nextScale = clamp(state.pinchStartScale * scaleRatio, 1, MAX_SCALE);

      const stageRect = stage.getBoundingClientRect();
      const offsetX = state.pinchCenterX - stageRect.left - state.stageWidth / 2;
      const offsetY = state.pinchCenterY - stageRect.top - state.stageHeight / 2;
      const ratioFromStart = nextScale / state.pinchStartScale;

      state.x = (state.pinchStartX - offsetX) * ratioFromStart + offsetX;
      state.y = (state.pinchStartY - offsetY) * ratioFromStart + offsetY;
      state.scale = nextScale;

      renderTransform();
      return;
    }

    if (state.isTouchPanning && event.touches.length === 1) {
      event.preventDefault();
      state.x = state.panStartX + (event.touches[0].clientX - state.panStartPointerX);
      state.y = state.panStartY + (event.touches[0].clientY - state.panStartPointerY);
      renderTransform();
    }
  };

  const handleTouchEnd = (event) => {
    if (!state.isOpen) return;
    hideHint();

    if (event.touches.length === 0) {
      state.isTouchPanning = false;
      state.isTouchPinching = false;
    } else if (event.touches.length === 1) {
      state.isTouchPinching = false;
      if (state.scale > 1) {
        state.isTouchPanning = true;
        state.panStartPointerX = event.touches[0].clientX;
        state.panStartPointerY = event.touches[0].clientY;
        state.panStartX = state.x;
        state.panStartY = state.y;
      }
    }

    const now = Date.now();
    if (event.changedTouches.length === 1 && event.touches.length === 0) {
      if (now - state.lastTouchEndAt < DOUBLE_TAP_MS) {
        event.preventDefault();
        const touch = event.changedTouches[0];
        const targetScale = state.scale > 1 ? 1 : 2;
        zoomToPoint(targetScale, touch.clientX, touch.clientY);
      }
      state.lastTouchEndAt = now;
    }
  };

  const handleTouchCancel = () => {
    state.isTouchPanning = false;
    state.isTouchPinching = false;
  };

  const handleStageClick = (event) => {
    if (!state.isOpen) return;
    if (event.target.closest('[data-size-guide-zoom-controls]')) return;
    if (event.target.closest('[data-size-guide-zoom-hint]')) return;
    if (isPointInsideImage(event.clientX, event.clientY)) return;
    closeViewer();
  };

  const isIntentionalTouchTap = () => {
    if (!state.docTouchEndAt || !state.docTouchStartAt) return true;

    const now = Date.now();
    const timeSinceTouchEnd = now - state.docTouchEndAt;
    const touchDuration = state.docTouchEndAt - state.docTouchStartAt;
    const scrollDelta = Math.abs(window.scrollY - state.docTouchStartScrollY);

    if (timeSinceTouchEnd > TAP_VALID_WINDOW_MS) return true;

    return !state.docTouchMoved && touchDuration <= TAP_MAX_DURATION_MS && scrollDelta <= TAP_SCROLL_THRESHOLD;
  };

  window.addEventListener('resize', () => {
    if (!state.isOpen || !viewer) return;
    fitImageToStage();
    renderTransform();
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('.size-guide-zoom-viewer')) return;

    const sourceImage = event.target.closest(IMAGE_SELECTOR);
    if (!sourceImage) return;
    if (!isIntentionalTouchTap()) return;

    event.preventDefault();
    openViewer(sourceImage);
  });

  document.addEventListener(
    'click',
    (event) => {
      const triggerButton = event.target.closest('.product-popup-modal__button');
      if (!triggerButton) return;
      if (!isIntentionalTouchTap()) return;

      const opener = triggerButton.closest('modal-opener[data-modal]');
      if (!opener) return;

      const modalSelector = opener.getAttribute('data-modal');
      if (!modalSelector) return;

      const modal = document.querySelector(modalSelector);
      if (!modal) return;

      const sourceImage = modal.querySelector('.product-popup-modal__content-info img');
      if (!sourceImage) return;

      // Evita abrir primero el modal intermedio: vamos directo al visor.
      event.preventDefault();
      event.stopImmediatePropagation();
      openViewer(sourceImage);
    },
    true
  );

  document.addEventListener(
    'touchstart',
    (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      state.docTouchStartX = touch.clientX;
      state.docTouchStartY = touch.clientY;
      state.docTouchStartAt = Date.now();
      state.docTouchEndAt = 0;
      state.docTouchStartScrollY = window.scrollY;
      state.docTouchMoved = false;
    },
    { passive: true }
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      if (state.docTouchMoved || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = Math.abs(touch.clientX - state.docTouchStartX);
      const deltaY = Math.abs(touch.clientY - state.docTouchStartY);
      const scrollDelta = Math.abs(window.scrollY - state.docTouchStartScrollY);

      if (deltaX > TAP_MOVE_THRESHOLD || deltaY > TAP_MOVE_THRESHOLD || scrollDelta > TAP_SCROLL_THRESHOLD) {
        state.docTouchMoved = true;
      }
    },
    { passive: true }
  );

  document.addEventListener(
    'touchend',
    () => {
      state.docTouchEndAt = Date.now();
    },
    { passive: true }
  );
})();
